import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Box, RefreshCw, Trash2, X } from 'lucide-react';
import { adminApi } from '../../services/api';
import { HostLoadDisplay } from './HostLoadDisplay';
import { formatHostLoadBytes } from '../../utils/hostLoad';

export function HostAgentDetailsModal({ hostAgentId, onClose }: { hostAgentId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const detail = useQuery({
    queryKey: ['host-agent-detail', hostAgentId],
    queryFn: () => adminApi.getHostAgent(hostAgentId),
    refetchInterval: 10_000,
  });
  const reconciliation = useQuery({
    queryKey: ['host-agent-reconciliation', hostAgentId],
    queryFn: () => adminApi.getHostAgentReconciliation(hostAgentId),
    retry: false,
  });
  const orphanMutation = useMutation({
    mutationFn: ({ action, target }: {
      action: 'adopt' | 'delete';
      target: { containerName: string; dockerId: string };
    }) => action === 'adopt'
      ? adminApi.adoptHostAgentOrphan(hostAgentId, target)
      : adminApi.deleteHostAgentOrphan(hostAgentId, target),
    onSuccess: async (_, variables) => {
      setMessage(`Orphan ${variables.target.containerName} ${variables.action === 'adopt' ? 'adopted' : 'deleted'}.`);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['host-agent-detail', hostAgentId] }),
        queryClient.invalidateQueries({ queryKey: ['host-agent-reconciliation', hostAgentId] }),
        queryClient.invalidateQueries({ queryKey: ['host-agents'] }),
      ]);
    },
    onError: (error: any) => setMessage(error?.response?.data?.error || error?.message || 'Orphan action failed'),
  });

  const data = detail.data;
  const report = reconciliation.data?.report;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content max-w-5xl max-h-[90vh] overflow-y-auto" onClick={event => event.stopPropagation()}>
        <div className="flex justify-between items-start p-6 border-b border-dark-600">
          <div>
            <h2 className="text-xl font-bold text-gray-100">{data?.hostAgent.name || 'Host Agent detail'}</h2>
            <p className="text-sm text-gray-500">Real allocation ledger, assigned servers and local reconciliation</p>
          </div>
          <button onClick={onClose} className="btn-ghost p-2"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-6">
          {detail.isLoading && <div className="spinner w-8 h-8 mx-auto" />}
          {detail.isError && <div className="text-sm text-red-400">{(detail.error as any)?.response?.data?.error || 'Failed to load Agent detail'}</div>}
          {message && <div className="p-3 rounded border border-dark-600 bg-dark-700 text-sm text-gray-300">{message}</div>}

          {data && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Metric label="Total weight" value={data.hostAgent.capacity.totalWeight} />
                <Metric label="Used weight" value={data.hostAgent.capacity.usedWeight} />
                <Metric label="Available" value={data.hostAgent.capacity.availableWeight} />
                <Metric label="Reservations" value={data.hostAgent.capacity.totalReservationCount} />
                <Metric label="Servers" value={data.allocations.length} />
              </div>

              <section>
                <h3 className="section-title mb-3">Reserved hard capacity</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <HardCapacityMetric
                    label="CPU"
                    reserved={data.hostAgent.capacity.hard.reserved.nanoCpus}
                    total={data.hostAgent.capacity.hard.total.nanoCpus}
                    available={data.hostAgent.capacity.hard.available.nanoCpus}
                    game={data.hostAgent.capacity.hard.gameReserved.nanoCpus}
                    custom={data.hostAgent.capacity.hard.customReserved.nanoCpus}
                    format={value => `${(value / 1_000_000_000).toFixed(2)} vCPU`}
                  />
                  <HardCapacityMetric
                    label="RAM"
                    reserved={data.hostAgent.capacity.hard.reserved.memoryBytes}
                    total={data.hostAgent.capacity.hard.total.memoryBytes}
                    available={data.hostAgent.capacity.hard.available.memoryBytes}
                    game={data.hostAgent.capacity.hard.gameReserved.memoryBytes}
                    custom={data.hostAgent.capacity.hard.customReserved.memoryBytes}
                    format={formatHostLoadBytes}
                  />
                  <HardCapacityMetric
                    label="Storage"
                    reserved={data.hostAgent.capacity.hard.reserved.storageBytes}
                    total={data.hostAgent.capacity.hard.total.storageBytes}
                    available={data.hostAgent.capacity.hard.available.storageBytes}
                    game={data.hostAgent.capacity.hard.gameReserved.storageBytes}
                    custom={data.hostAgent.capacity.hard.customReserved.storageBytes}
                    format={formatHostLoadBytes}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  GameServer weight is separate. Active game reservations and all custom reservations consume CPU/RAM; stopped custom instances keep their full pinned profile.
                </p>
              </section>

              <section>
                <h3 className="section-title mb-3">Current physical load</h3>
                <HostLoadDisplay agent={data.hostAgent} />
                <p className="text-xs text-gray-500 mt-2">
                  Physical CPU/RAM observation; allocation weight and reservations are shown separately.
                </p>
              </section>

              <section>
                <h3 className="section-title mb-3">GameServer mobility readiness</h3>
                <div className="card p-4 space-y-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className={data.hostAgent.mobility.fullReady ? 'badge-online' : 'badge-offline'}>
                      {data.hostAgent.mobility.fullReady ? 'Full capability ready' : 'Incomplete capability'}
                    </span>
                    <span className={data.mobilityPolicy.enabled ? 'text-neon-400 text-sm' : 'text-yellow-300 text-sm'}>
                      New mobility admission {data.mobilityPolicy.enabled ? 'enabled' : 'disabled'} · rollout {data.mobilityPolicy.rolloutPercent}%
                    </span>
                  </div>
                  {!data.hostAgent.mobility.fullReady && (
                    <p className="text-xs text-yellow-300">{data.hostAgent.mobility.reasons.join(' · ')}</p>
                  )}
                  <p className="text-xs text-gray-500 break-words">
                    capabilities: {data.hostAgent.capabilities.length > 0 ? data.hostAgent.capabilities.join(' · ') : 'none'}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                    <div className="p-3 bg-dark-700 rounded">
                      <div className="text-xs text-gray-500 uppercase">Placement sync</div>
                      <div className="text-gray-200 mt-1">{data.hostAgent.placementSync?.status || 'not reported'}</div>
                      <div className="text-xs text-gray-500">
                        snapshot {data.hostAgent.placementSync?.snapshotRevision ?? '—'} / ledger {data.hostAgent.capacity.revision}
                      </div>
                      {(data.hostAgent.placementSync?.pending || data.hostAgent.placementSync?.conflicts) ? (
                        <div className="text-xs text-yellow-300 mt-1">
                          {data.hostAgent.placementSync.pending ?? 0} pending · {data.hostAgent.placementSync.conflicts ?? 0} conflicts
                        </div>
                      ) : null}
                    </div>
                    <div className="p-3 bg-dark-700 rounded">
                      <div className="text-xs text-gray-500 uppercase">Checkpoint capture</div>
                      <div className="text-gray-200 mt-1">
                        {data.hostAgent.mobilityCheckpoint
                          ? `${data.hostAgent.mobilityCheckpoint.storageDriver} · max ${data.hostAgent.mobilityCheckpoint.maxConcurrentCaptures}`
                          : 'not ready'}
                      </div>
                      {data.hostAgent.mobilityCheckpoint && <CopyCode value={data.hostAgent.mobilityCheckpoint.storageNamespaceId} label="namespace" />}
                    </div>
                    <div className="p-3 bg-dark-700 rounded">
                      <div className="text-xs text-gray-500 uppercase">Target hydrate</div>
                      <div className="text-gray-200 mt-1">
                        {data.hostAgent.mobilityRelocation?.ready
                          ? `ready · max ${data.hostAgent.mobilityRelocation.maxConcurrentHydrates}`
                          : 'not ready'}
                      </div>
                      <div className="text-xs text-gray-500">{data.hostAgent.mobilityRelocation?.contract || 'contract unavailable'}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Metric label="RAM headroom" value={formatHostLoadBytes(data.mobilityPolicy.admission.memoryHeadroomBytes)} />
                    <Metric label="Projected CPU max" value={`${data.mobilityPolicy.admission.maxProjectedCpuPercent}%`} />
                    <Metric label="CPU pressure" value={`${data.mobilityPolicy.admission.cpuPressureMinSamples} samples / ${Math.round(data.mobilityPolicy.admission.cpuPressureWindowMs / 1000)}s`} />
                    <Metric label="Load freshness" value={`${Math.round(data.mobilityPolicy.admission.freshnessMs / 1000)}s`} />
                  </div>
                  <p className="text-xs text-gray-500">
                    CPU pressure samples: {data.hostAgent.admissionState?.consecutiveHighSamples ?? 0}
                    {data.hostAgent.admissionState?.lastDecisionReason ? ` · ${data.hostAgent.admissionState.lastDecisionReason}` : ''}
                  </p>
                </div>
              </section>

              <section>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="section-title">Assigned servers</h3>
                  <button className="btn-ghost text-xs" onClick={() => detail.refetch()}><RefreshCw className="w-3 h-3" /> Refresh</button>
                </div>
                <div className="overflow-x-auto border border-dark-600 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-dark-700/60 text-xs text-gray-500 uppercase"><tr>
                      <th className="p-3 text-left">Server</th><th className="p-3 text-left">State</th>
                      <th className="p-3 text-left">Package / owner</th><th className="p-3 text-left">Capacity / ports</th>
                      <th className="p-3 text-left">Correlation</th>
                    </tr></thead>
                    <tbody className="divide-y divide-dark-700">
                      {data.allocations.map(server => (
                        <tr key={server.serverId}>
                          <td className="p-3"><div className="text-gray-200">{server.name}</div><code className="text-xs text-gray-500">{server.serverId}</code></td>
                          <td className="p-3"><span className={server.actualState === 'running' ? 'badge-online' : 'badge-offline'}>{server.actualState}</span><div className="text-xs text-gray-500 mt-1">desired {server.desiredState}</div></td>
                          <td className="p-3 text-gray-400"><div>{server.package?.displayName || server.package?.name || 'Unknown package'}</div><div className="text-xs text-gray-500">{server.user?.email || server.user?.name || 'Unknown owner'}</div></td>
                          <td className="p-3 text-gray-400"><div>weight {server.capacityReservation?.weight ?? '?'} · {server.capacityReservation?.state || 'unknown'}</div><div className="text-xs text-gray-500">revision {server.capacityReservation?.placementRevision ?? '—'} · {server.capacityReservation?.ports?.map(port => `${port.hostPort}/${port.protocol}`).join(', ') || server.port || 'no ports'}</div>{server.capacityReservation?.resources && <div className="text-xs text-gray-500">CPU {(server.capacityReservation.resources.nanoCpus / 1_000_000_000).toFixed(2)} · RAM {formatHostLoadBytes(server.capacityReservation.resources.memoryBytes)} · storage {formatHostLoadBytes(server.capacityReservation.resources.storageBytes)}</div>}<CopyCode value={server.capacityReservation?.reservationId || null} label="reservation" /></td>
                          <td className="p-3"><CopyCode value={server.actionId} label="action" /><CopyCode value={server.operationId} label="operation" /></td>
                        </tr>
                      ))}
                      {data.allocations.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-gray-500">No assigned servers</td></tr>}
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <h3 className="section-title mb-3">Exact capacity reservation ledger</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {data.capacityReservations.map((reservation, index) => <div key={reservation.reservationId || `${reservation.serverId}-${index}`} className="p-3 bg-dark-700 rounded text-sm">
                    <div className="flex items-center justify-between gap-3"><span className="text-gray-200">{reservation.state || 'legacy'}</span><span className="text-xs text-gray-500">revision {reservation.placementRevision ?? '—'}</span></div>
                    <CopyCode value={reservation.reservationId} label="reservation" />
                    <CopyCode value={reservation.serverId} label="server" />
                    <div className="text-xs text-gray-500 mt-1">weight {reservation.weight} · ports {reservation.ports.map(port => `${port.hostPort}/${port.protocol}`).join(', ') || 'none'}</div>
                    {reservation.resources && <div className="text-xs text-gray-500">CPU {(reservation.resources.nanoCpus / 1_000_000_000).toFixed(2)} · RAM {formatHostLoadBytes(reservation.resources.memoryBytes)} · storage {formatHostLoadBytes(reservation.resources.storageBytes)}</div>}
                  </div>)}
                  {data.capacityReservations.length === 0 && <p className="text-sm text-gray-500">No persisted GameServer reservations.</p>}
                </div>
              </section>

              <section>
                <h3 className="section-title mb-3">Custom capacity reservation ledger</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {data.customCapacityReservations.map(reservation => <div key={reservation.instanceId} className="p-3 bg-dark-700 rounded text-sm">
                    <CopyCode value={reservation.instanceId} label="instance" />
                    <div className="text-xs text-gray-500 mt-1">ports {reservation.ports.map(port => `${port.hostPort}/${port.protocol}`).join(', ') || 'none'}</div>
                    <div className="text-xs text-gray-500">CPU {(reservation.resources.nanoCpus / 1_000_000_000).toFixed(2)} · RAM {formatHostLoadBytes(reservation.resources.memoryBytes)} · storage {formatHostLoadBytes(reservation.resources.storageBytes)}</div>
                  </div>)}
                  {data.customCapacityReservations.length === 0 && <p className="text-sm text-gray-500">No persisted Custom Instance reservations.</p>}
                </div>
              </section>
            </>
          )}

          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="section-title">Local reconciliation</h3>
              <button className="btn-secondary text-xs" disabled={reconciliation.isFetching} onClick={() => reconciliation.refetch()}><RefreshCw className={`w-3 h-3 ${reconciliation.isFetching ? 'animate-spin' : ''}`} /> Read Agent report</button>
            </div>
            {reconciliation.isError && <div className="p-3 border border-yellow-500/20 bg-yellow-500/10 rounded text-sm text-yellow-300">Agent report unavailable: {(reconciliation.error as any)?.response?.data?.error || (reconciliation.error as Error).message}</div>}
            {report && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Metric label="Mapped inventory" value={report.inventory.length} />
                  <Metric label="Orphans" value={report.orphans.length} />
                  <Metric label="Conflicts" value={report.conflicts.length} />
                  <Metric label="Ignored Docker" value={report.ignoredDockerContainers} />
                </div>
                {report.conflicts.map((conflict, index) => <div key={`${conflict.code}-${index}`} className="flex gap-2 text-sm text-yellow-300"><AlertTriangle className="w-4 h-4 shrink-0" /><span>{conflict.code}: {conflict.message}</span></div>)}
                {report.orphans.map(orphan => (
                  <div key={orphan.dockerId} className="p-4 border border-dark-600 rounded-lg flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div><div className="flex items-center gap-2 text-gray-200"><Box className="w-4 h-4" /> {orphan.containerName}</div><code className="text-xs text-gray-500">{orphan.dockerId}</code><p className="text-xs text-gray-500 mt-1">{orphan.reason || `${orphan.image} · ${orphan.observedState}`}</p></div>
                    <div className="flex gap-2">
                      <button disabled={!orphan.adoptable || orphanMutation.isPending} className="btn-primary text-xs" onClick={() => orphanMutation.mutate({ action: 'adopt', target: orphan })}>Adopt</button>
                      <button disabled={orphanMutation.isPending} className="btn-ghost text-xs text-red-400" onClick={() => confirm(`Delete orphan container ${orphan.containerName}? Storage is not deleted automatically.`) && orphanMutation.mutate({ action: 'delete', target: orphan })}><Trash2 className="w-3 h-3" /> Delete Docker orphan</button>
                    </div>
                  </div>
                ))}
                {report.orphans.length === 0 && <p className="text-sm text-gray-500">No local Docker orphans reported.</p>}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return <div className="p-3 bg-dark-700 rounded"><div className="text-xs text-gray-500">{label}</div><div className="text-lg font-semibold text-gray-200">{value}</div></div>;
}

function HardCapacityMetric({
  label,
  reserved,
  total,
  available,
  game,
  custom,
  format,
}: {
  label: string;
  reserved: number;
  total: number;
  available: number;
  game: number;
  custom: number;
  format: (value: number) => string;
}) {
  const percent = total > 0 ? Math.round((reserved / total) * 100) : 0;
  return <div className="p-3 bg-dark-700 rounded text-sm"><div className="flex items-center justify-between gap-3"><span className="text-gray-300">{label}</span><span className={percent >= 100 ? 'text-red-300' : 'text-gray-400'}>{percent}%</span></div><div className="text-gray-200 mt-1">{format(reserved)} / {format(total)}</div><div className="progress-bar mt-2"><div className={`progress-fill ${percent >= 100 ? 'progress-fill-danger' : ''}`} style={{ width: `${Math.min(Math.max(percent, 0), 100)}%` }} /></div><div className="text-xs text-gray-500 mt-2">game {format(game)} · custom {format(custom)}</div><div className="text-xs text-gray-500">available {format(available)}</div></div>;
}

function CopyCode({ value, label }: { value: string | null; label: string }) {
  if (!value) return null;
  return <button className="block text-left text-xs text-gray-500 hover:text-gray-300 max-w-[180px] truncate" title={`Copy ${label} ID`} onClick={() => navigator.clipboard?.writeText(value)}><span className="text-gray-600">{label}: </span>{value}</button>;
}
